import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  Users,
  Wallet,
  ShoppingBag,
  Truck,
  Gift,
  CheckCircle2,
  ArrowRight,
  Loader2,
  XCircle,
} from "lucide-react";
import Footer from "../components/Footer";
import { API } from "../App";

const LOGO_URL =
  "https://static.prod-images.emergentagent.com/jobs/203f7def-ad2c-4f8f-807f-2bc10f2f16d6/images/a913d16ba95302b639f95999ec0d2a7b5d654d2a1647e0be42e9b257d09c7d8a.png";

const USER_TYPE_LABELS = {
  user: { label: "Cliente", color: "bg-orange-100 text-orange-700" },
  merchant: { label: "Lojista", color: "bg-purple-100 text-purple-700" },
  driver: { label: "Entregador", color: "bg-yellow-100 text-yellow-700" },
  admin: { label: "Fundador", color: "bg-rose-100 text-rose-700" },
};

/**
 * Public invite landing page — /indicado-por/:code
 * Shows who invited, MLM benefits, and a big CTA to register.
 */
const InvitePage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [referrer, setReferrer] = useState(null);
  const [errorState, setErrorState] = useState(null); // 'not_found' | 'network'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/auth/referral-info/${code}`);
        if (!r.ok) {
          if (!cancelled) setErrorState(r.status === 404 ? "not_found" : "network");
          return;
        }
        const data = await r.json();
        if (!cancelled) setReferrer(data);
      } catch {
        if (!cancelled) setErrorState("network");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const getInitials = (name = "") =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase() || "")
      .join("") || "GF";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 to-orange-100">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  if (errorState === "not_found") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
        <XCircle className="w-16 h-16 text-red-400 mb-4" />
        <h1 className="font-heading text-2xl font-bold mb-2">Convite inválido</h1>
        <p className="text-gray-600 text-center mb-6 max-w-sm">
          Este link de convite não foi encontrado. Pode ter sido digitado com erro
          ou o código não existe mais. Você pode se cadastrar diretamente sem código.
        </p>
        <Button asChild className="bg-primary">
          <Link to="/register" data-testid="invite-fallback-register">Ir para o cadastro</Link>
        </Button>
      </div>
    );
  }

  if (errorState === "network") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
        <Loader2 className="w-12 h-12 text-amber-500 mb-4 animate-spin" />
        <h1 className="font-heading text-2xl font-bold mb-2">Não foi possível verificar o convite</h1>
        <p className="text-gray-600 text-center mb-6 max-w-sm">
          Tivemos um problema momentâneo de rede. Verifique sua conexão e tente
          recarregar a página, ou cadastre-se diretamente.
        </p>
        <div className="flex gap-3">
          <Button onClick={() => window.location.reload()} className="bg-primary">
            Tentar novamente
          </Button>
          <Button asChild variant="outline">
            <Link to="/register">Cadastrar sem código</Link>
          </Button>
        </div>
      </div>
    );
  }

  const typeInfo = USER_TYPE_LABELS[referrer.user_type] || USER_TYPE_LABELS.user;

  return (
    <div className="min-h-screen bg-white" data-testid="invite-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-orange-500 to-orange-600 text-white py-14 md:py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-yellow-300 blur-3xl" />
        </div>

        <div className="container mx-auto px-6 relative">
          <Link to="/" className="inline-flex items-center gap-3 mb-10">
            <img loading="lazy" decoding="async" src={LOGO_URL} alt="GoFood" className="w-10 h-10 rounded-lg bg-white p-1" />
            <span className="font-heading text-2xl font-bold">GoFood</span>
          </Link>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
              <Sparkles className="w-4 h-4" />
              Você recebeu um convite especial
            </div>

            {/* Referrer card */}
            <div className="bg-white/15 backdrop-blur border border-white/30 rounded-2xl p-5 mb-6 flex items-center gap-4 max-w-md" data-testid="invite-referrer-card">
              <div className="w-16 h-16 rounded-full bg-white text-primary flex items-center justify-center font-bold text-2xl shadow-lg shrink-0">
                {getInitials(referrer.name)}
              </div>
              <div className="min-w-0">
                <p className="text-xs opacity-90 uppercase tracking-wider">Convidado por</p>
                <p className="font-heading text-xl font-bold truncate" data-testid="invite-referrer-name">
                  {referrer.name}
                </p>
                <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-semibold mt-1 ${typeInfo.color}`}>
                  {typeInfo.label}
                </span>
              </div>
            </div>

            <h1 className="font-heading text-4xl md:text-6xl font-black leading-tight mb-4">
              Peça, Indique & <span className="text-yellow-300">Ganhe</span>
            </h1>
            <p className="text-lg md:text-xl opacity-95 mb-8 max-w-xl">
              Ao se cadastrar usando este convite, você entra direto na rede MLM de
              5 níveis e ganha com cada indicação sua a partir de agora.
            </p>

            <div className="flex flex-wrap gap-3">
              <Button
                size="lg"
                onClick={() => navigate(`/register/${code}`)}
                className="bg-white text-primary hover:bg-gray-100 rounded-full px-8 shadow-xl"
                data-testid="invite-cta-register"
              >
                Criar Minha Conta Grátis
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="bg-transparent border-white/60 text-white hover:bg-white/10 rounded-full px-6"
              >
                <Link to="/login">Já tenho conta</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="container mx-auto px-6 py-16">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-3">
            Por que você deveria entrar no GoFood agora?
          </h2>
          <p className="text-gray-600 text-lg">
            Delivery completo + Sistema de indicação que paga em PIX direto pra você.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {[
            {
              icon: Users,
              title: "Rede MLM em 5 níveis",
              desc: "Ganhe um percentual sobre os pedidos de quem você indica — e de quem eles indicam, até o 5º nível.",
              color: "from-orange-500 to-amber-500",
            },
            {
              icon: Wallet,
              title: "Saques via PIX",
              desc: "Receba direto na sua chave PIX via ASAAS, sem taxas escondidas. Valor mínimo: R$ 20,00.",
              color: "from-emerald-500 to-green-600",
            },
            {
              icon: ShoppingBag,
              title: "Cliente ou Embaixador",
              desc: "Use para pedir comida e indique amigos. Cada novo cadastro é uma fonte recorrente de comissão.",
              color: "from-purple-500 to-indigo-500",
            },
            {
              icon: Truck,
              title: "Vire Entregador",
              desc: "Tem moto ou bicicleta? Comece a entregar e receba por corrida + bônus dos seus indicados.",
              color: "from-yellow-500 to-orange-500",
            },
            {
              icon: ShoppingBag,
              title: "Seja Lojista",
              desc: "Tem um restaurante? Venda no GoFood com taxa competitiva e monte sua própria rede.",
              color: "from-rose-500 to-pink-500",
            },
            {
              icon: Gift,
              title: "100% Gratuito",
              desc: "Cadastro zero. Sem mensalidade para clientes. Lojistas pagam plano único acessível.",
              color: "from-blue-500 to-cyan-500",
            },
          ].map((b) => (
            <div
              key={b.title}
              className="group bg-white border-2 border-gray-100 rounded-2xl p-6 hover:border-primary/40 hover:shadow-xl transition-all"
            >
              <div
                className={`w-12 h-12 rounded-xl bg-gradient-to-br ${b.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
              >
                <b.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading text-lg font-bold mb-2">{b.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">
            Como começar em 3 passos
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { num: "1", title: "Clique em Cadastrar", desc: "Informe seus dados, CPF e chave PIX (é rápido)." },
              { num: "2", title: "Escolha seu perfil", desc: "Cliente, Entregador ou Lojista — você decide." },
              { num: "3", title: "Compartilhe seu link", desc: "Ganhe comissão sobre cada indicação sua, até 5 níveis abaixo." },
            ].map((s) => (
              <div key={s.num} className="text-center">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 font-heading text-2xl font-black shadow-lg">
                  {s.num}
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              size="lg"
              onClick={() => navigate(`/register/${code}`)}
              className="bg-primary hover:bg-primary/90 rounded-full px-10 shadow-xl"
              data-testid="invite-cta-register-bottom"
            >
              Cadastrar agora com o convite de {referrer.name.split(" ")[0]}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-gray-500 mt-4 flex items-center justify-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-green-600" />
              Nenhuma cobrança • Cancele quando quiser • LGPD compliant
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InvitePage;
